import React from 'react';
import { createSelector } from 'reselect';

import { createReduceSelector } from 'utils/redux';
import { IncludeContent } from 'bricks/IncludeContent';

/**
 * Replace values in object in Object.keys order
 */
const zipValues = (obj, values) => {
  let index = 0;
  return Object.keys(obj).reduce((result, name) => {
    result[name] = values[index];
    index += 1;
    return result;
  }, {});
};

/**
 * A step down from createMarkupSelector
 * A lower level selector that returns function with one argument - replaceTags
 */
export const createMarkupAdjustableSelector = (templateSelector, replaceSelectors) =>
  createSelector(
    templateSelector,
    ...Object.values(replaceSelectors || {}),
    (template, ...values) =>
      /** returns this function */
      (replaceTags) => (
        <IncludeContent
          htmlContent={template}
          replaceTable={zipValues(replaceSelectors || {}, values)}
          replaceTags={replaceTags}
        />
      ),
  );

/**
 * Returns a new reselector, that parses html in `templateSelector`
 *  and replaces all the insertions by values of selectors in `replaceSelectors`
 * @param templateSelector - a selector for html markup with {{replaceMe}} insertions
 * @param replaceSelectors - an object of [insertionName]: selector for replacement string
 * @param props - props passed to include content
 * @returns React `node`
 */
export const createMarkupSelector = (templateSelector, replaceSelectors = {}, props) =>
  createSelector(
    templateSelector,
    ...Object.values(replaceSelectors || {}),
    (template, ...values) => (
      <IncludeContent
        {...props}
        htmlContent={template}
        replaceTable={zipValues(replaceSelectors || {}, values)}
      />
    ),
  );

/**
 * Combination of `adjustableMarkup` and `reduce` selectors
 * @param templateSelector - either normal or reduce selector that returns template string, with placeholders
 * @param replaceSelectors - dict where key is placeholder and value is normal/reduce data-selector
 * @returns (replaceTags) => React Node
 */
export const createAdjustableMarkupReduceSelector = (templateSelector, replaceSelectors = {}) =>
  createReduceSelector(
    templateSelector,
    ...Object.values(replaceSelectors),
    (template = '', ...values) =>
      /** returns this function */
      (replaceTags) => (
        <IncludeContent
          htmlContent={template}
          replaceTable={zipValues(replaceSelectors, values)}
          replaceTags={replaceTags}
        />
      ),
  );

/**
 * Combination of `adjustableMarkup` and `reduce` selectors
 * @param templateSelector - either normal or reduce selector that returns template string, with placeholders
 * @param replaceSelectors - dict where key is placeholder and value is normal/reduce data-selector
 * @returns React Node
 */
export const createMarkupReduceSelector = (templateSelector, replaceSelectors = {}) =>
  createReduceSelector(
    templateSelector,
    ...Object.values(replaceSelectors),
    (template = '', ...values) => (
      <IncludeContent htmlContent={template} replaceTable={zipValues(replaceSelectors, values)} />
    ),
  );
